import React, { ReactNode, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useLayout } from "../../hooks/useLayout";
import { useLocale } from "../../hooks/useLocale";

import { Footer, Header, Toggle } from "@in/component-library";
import { Grid, GridItem, Icon } from "@innovationnorway/lighthouse-components";
import useSWR from "swr";
import { AppContext } from "../../../app-context";
import { setLocalStorage } from "../../../utils/localstorage";
import { sendSiteimproveEvent } from "../../../utils/sendSiteimproveEvent";
import { getUserAccounts } from "../../apis/getUserAccounts";
import Loader from "../../components/loader/loader";
import CookieSettings from "../cookieSettings/cookieSettings";
import SkipToContent from "../skipToContent/skipToContent";
import "./layout.scss";

interface LayoutProps {
  showMenu?: boolean;
  children?: ReactNode;
}

const Layout = ({ showMenu = true, children }: LayoutProps) => {
  const navigate = useNavigate();
  const { account } = useContext(AppContext);
  const { locale, setLocale } = useLocale();
  const { layout, loading, translations, mutate } = useLayout();
  const { mutate: mutateUserAccounts } = useSWR(`userAccounts`, getUserAccounts);
  const beta = window.location.pathname === "/beta";

  useEffect(() => {
    mutate();
    mutateUserAccounts();
  }, [locale]);

  return loading || !layout ? (
    <Loader />
  ) : (
    <div className="betaPage">
      <span className="top-element" tabIndex={-1} />
      <SkipToContent />
      <Header
        name={account?.name}
        companyName={showMenu ? account?.companyName : undefined}
        loggedInAsLabel={layout.loggedInHeading}
        logoAriaLabel={layout.logoAriaLabel}
        logoLink={layout.logoUrl}
        closeDropdownOnClick={true}
        className="layout__header"
        hotJarClass="data-hj-suppress"
        languages={[
          {
            code: "nb",
            isSelected: locale === "nb",
            name: "Bokmål",
            onClick: () => setLocale("nb")
          },
          {
            code: "nn",
            isSelected: locale === "nn",
            name: "Nynorsk",
            onClick: () => setLocale("nn")
          },
          {
            code: "en",
            isSelected: locale === "en",
            name: "English",
            onClick: () => setLocale("en")
          }
        ]}
        menuItems={[
          {
            text: layout.logOutLabel,
            onClick: () => navigate("/single-sign-out")
          }
        ]}
      />
      <div className="beta">
        <div className="beta__header">
          <Icon prefix="fasr" iconName="wrench" className="beta__header__icon" />
          {translations.betaHomeTitle}
        </div>
        <Toggle
          className="beta__toggle"
          value={beta}
          onClick={() => {
            sendSiteimproveEvent(`MyPage-betaActivation`, "BetaClicked", "Beta deaktivert");
            setLocalStorage("beta", "false");
            navigate("/");
          }}
          label="Betaversjon"
        />
      </div>
      <div style={{ marginLeft: "40px", marginRight: "40px" }}>
        <Grid>
          <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
            {children}
          </GridItem>
        </Grid>
      </div>

      <Footer className="layout__footer">
        <div dangerouslySetInnerHTML={{ __html: layout.footerText1 }} />
        <div>
          <div dangerouslySetInnerHTML={{ __html: layout.footerText2 }} />
          <CookieSettings />
        </div>
      </Footer>
    </div>
  );
};

export default Layout;
